import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaAward,
  FaChartLine,
  FaCheckCircle,
  FaCube,
  FaEye,
  FaFileAlt,
  FaIndustry,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaSearch,
  FaStar,
  FaUsers,
} from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/buyer_contract_manufacturers_page.scss";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import { INDUSTRY_TYPE_MAP } from "../../constants/industryTypeMap";
import { useProductDetailsMaster } from "../../hooks/useProductDetailsMaster";
import {
  formatMetric,
  getCapacity,
  getCertificationShortLabels,
  getExperienceSummary,
  getMoqSummary,
  getQuoteSubmittedMeta,
  getResponseTime,
  getWorkforceSummary,
} from "../../utils/contractManufacturerCardHelpers";

const TAB_ALL = "all";
const TAB_SUBMITTED = "submitted";
const TAB_FEATURED = "featured";

export default function BuyerContractManufacturersPage() {
  const { dimensionUnitOptions } = useProductDetailsMaster();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [list, setList] = useState([]);
  const [rfqSentCompanyIds, setRfqSentCompanyIds] = useState(new Set());
  const [submissionMetaByCompanyId, setSubmissionMetaByCompanyId] = useState(new Map());

  const [activeTab, setActiveTab] = useState(TAB_ALL);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("relevance");
  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [selectedState, setSelectedState] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [listRes, submissionsRes] = await Promise.allSettled([
          CommonService.get(API_URLS.CONTRACT_MANUFACTURER.LIST, { page: 1, limit: 50 }),
          CommonService.get(API_URLS.CONTRACT_MANUFACTURER.MY_RFQ_SUBMISSIONS, { limit: 500, offset: 0 }),
        ]);

        const listData =
          listRes.status === "fulfilled"
            ? (listRes.value?.data ?? listRes.value)
            : {};
        const items = Array.isArray(listData?.items) ? listData.items : [];
        if (!cancelled) setList(items);

        if (submissionsRes.status === "fulfilled") {
          const submissionsData = submissionsRes.value?.data ?? submissionsRes.value ?? {};
          const submissionItems = Array.isArray(submissionsData?.items) ? submissionsData.items : [];
          const ids = new Set(
            submissionItems
              .map((row) => Number(row?.company_id))
              .filter((n) => Number.isFinite(n) && n > 0),
          );
          if (!cancelled) setRfqSentCompanyIds(ids);

          const metaMap = new Map();
          submissionItems.forEach((row) => {
            const companyId = Number(row?.company_id);
            if (!Number.isFinite(companyId) || companyId <= 0) return;
            const normalizedRow = {
              ...row,
              quoteSubmitted: Boolean(row?.quoteSubmitted || row?.quote_submitted || row?.quote_table_id || row?.quote_status),
              quote_submitted: Boolean(row?.quoteSubmitted || row?.quote_submitted || row?.quote_table_id || row?.quote_status),
            };
            const existing = metaMap.get(companyId);
            const currentQuoted = Boolean(
              normalizedRow?.quoteSubmitted || normalizedRow?.quote_submitted || normalizedRow?.quote_table_id || normalizedRow?.quote_status
            );
            const prevQuoted = Boolean(
              existing?.quoteSubmitted || existing?.quote_submitted || existing?.quote_table_id || existing?.quote_status
            );
            const currentTs =
              normalizedRow?.quoteSubmittedAt ||
              normalizedRow?.quote_submitted_at ||
              normalizedRow?.quote_updated_at ||
              normalizedRow?.updated_at ||
              normalizedRow?.created_at ||
              null;
            const prevTs =
              existing?.quoteSubmittedAt || existing?.quote_submitted_at || existing?.quote_updated_at || existing?.updated_at || existing?.created_at || null;
            const currentTime = currentTs ? new Date(currentTs).getTime() : 0;
            const prevTime = prevTs ? new Date(prevTs).getTime() : 0;
            if (!existing || (currentQuoted && !prevQuoted) || (currentQuoted === prevQuoted && currentTime >= prevTime)) {
              metaMap.set(companyId, normalizedRow);
            }
          });
          if (!cancelled) setSubmissionMetaByCompanyId(metaMap);
        } else if (!cancelled) {
          setRfqSentCompanyIds(new Set());
          setSubmissionMetaByCompanyId(new Map());
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || "Unable to load contract manufacturers");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const looksLikeLocationId = (v) => {
    if (v == null || v === "") return false;
    return /^\d+$/.test(String(v).trim());
  };

  const getLocation = (item) => {
    const fd = item?.facility_details || {};
    const cityLabel = fd.cityName || fd.city_name || fd.cityLabel || fd.displayCity;
    const stateLabel = fd.stateName || fd.state_name || fd.stateLabel || fd.displayState;
    if (cityLabel || stateLabel) return [cityLabel, stateLabel].filter(Boolean).join(", ");

    const cityRaw = fd.city ?? fd.address?.city;
    const stateRaw = fd.state ?? fd.address?.state;
    if (!looksLikeLocationId(cityRaw) && !looksLikeLocationId(stateRaw)) {
      if (cityRaw || stateRaw) return [cityRaw, stateRaw].filter(Boolean).join(", ");
    }

    const addr = item?.address || fd.address;
    if (typeof addr === "string" && addr.trim()) return addr.trim();
    if (addr && typeof addr === "object") {
      const parts = [addr.city_name || addr.cityName, addr.state_name || addr.stateName, addr.country_name || addr.countryName].filter(Boolean);
      if (parts.length) return parts.join(", ");
    }
    return "";
  };

  const getItemStateForFilter = (item) => {
    const fd = item?.facility_details || {};
    const stateLabel = fd.stateName || fd.state_name || fd.stateLabel || fd.displayState;
    if (stateLabel && String(stateLabel).trim()) return String(stateLabel).trim();
    const addr = item?.address || fd.address;
    if (addr && typeof addr === "object") {
      const st = addr.state_name || addr.stateName || addr.state;
      if (st && !looksLikeLocationId(st)) return String(st).trim();
    }
    const stateRaw = fd.state ?? fd.address?.state;
    if (stateRaw && !looksLikeLocationId(stateRaw)) return String(stateRaw).trim();
    return "";
  };

  const industryLabel = (val) => {
    if (!val) return "";
    if (typeof val === "object" && val.label) return val.label;
    const found = INDUSTRY_TYPE_MAP.find((o) => o.value === val || o.label === val);
    return found ? found.label : String(val);
  };

  const getIndustryTags = (item) => {
    const fd = item?.facility_details || {};
    const raw = fd.industryTypes || fd.industry_types || [];
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw
      .slice(0, 3)
      .map((x) => industryLabel(typeof x === "string" ? x : x?.value || x?.label || x))
      .filter(Boolean);
  };

  const isFeaturedManufacturer = (item) => {
    const fd = item?.facility_details || {};
    return Boolean(item?.featured || fd?.featured || fd?.isFeatured || fd?.listing_featured || fd?.listingFeatured);
  };

  // Best-effort: show banner when API exposes submission or quote metadata.
  const isQuoteSubmitted = (item) =>
    Boolean(
      item?.quoteSubmitted ||
        item?.quote_submitted ||
        item?.quote_table_id ||
        item?.quoteTableId ||
        item?.quote_status ||
        item?.quoteStatus ||
        String(item?.status || "").toLowerCase() === "responded" ||
        String(item?.status || "").toLowerCase() === "quoted",
        item?.rfqSubmitted ||
        item?.rfq_submitted ||
        item?.hasSubmittedQuote ||
        item?.submitted_quote ||
        item?.submittedQuote ||
        item?.quote_submitted_at ||
        item?.quoteSubmittedAt ||
        item?.submitted_quote_amount ||
        item?.submittedQuoteAmount,
    );

  const getRatingDisplay = (item) => {
    const fd = item?.facility_details || {};
    const raw = fd.ratingScore ?? fd.rating_score ?? fd.avgRating ?? item?.rating_score ?? item?.rating;
    const n = raw != null && raw !== "" ? Number(raw) : NaN;
    if (!Number.isFinite(n) || n <= 0) return null;
    return n.toFixed(1);
  };

  const getReviewCount = (item) => {
    const fd = item?.facility_details || {};
    const n = fd.reviewCount ?? fd.review_count ?? item?.review_count;
    const num = n != null && n !== "" ? parseInt(String(n), 10) : NaN;
    if (!Number.isFinite(num) || num < 0) return null;
    return num;
  };

  const uniqueStates = useMemo(() => {
    const set = new Set();
    list.forEach((it) => {
      const st = getItemStateForFilter(it);
      if (st) set.add(st);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [list]);

  const filteredList = useMemo(() => {
    const enrichedList = list.map((item) => {
      const companyId = Number(item?.company_id);
      const meta = Number.isFinite(companyId) ? submissionMetaByCompanyId.get(companyId) : null;
      return meta ? { ...item, ...meta } : item;
    });
    const q = search.trim().toLowerCase();
    const out = enrichedList.filter((item) => {
      if (q) {
        const name = String(item?.company_name || "").toLowerCase();
        const loc = String(getLocation(item) || "").toLowerCase();
        if (!name.includes(q) && !loc.includes(q)) return false;
      }
      if (selectedIndustry) {
        const fd = item?.facility_details || {};
        const raw = fd.industryTypes || fd.industry_types || [];
        const values = Array.isArray(raw)
          ? raw.map((x) => (typeof x === "string" ? x : x?.value || x?.label || x)).map((v) => String(v || "").trim()).filter(Boolean)
          : [];
        const normalized = values
          .map((v) => (INDUSTRY_TYPE_MAP.find((o) => o.value === v || o.label === v)?.value || v))
          .filter(Boolean);
        if (!normalized.includes(selectedIndustry)) return false;
      }
      if (selectedState) {
        if (getItemStateForFilter(item) !== selectedState) return false;
      }
      return true;
    });

    const sorted = [...out];
    if (sortBy === "name") {
      sorted.sort((a, b) => String(a?.company_name || "").localeCompare(String(b?.company_name || ""), undefined, { sensitivity: "base" }));
    } else if (sortBy === "rating") {
      sorted.sort((a, b) => {
        const ra = parseFloat(getRatingDisplay(a) || "0");
        const rb = parseFloat(getRatingDisplay(b) || "0");
        if (rb !== ra) return rb - ra;
        return String(a?.company_name || "").localeCompare(String(b?.company_name || ""), undefined, { sensitivity: "base" });
      });
    }
    return sorted;
  }, [list, submissionMetaByCompanyId, search, selectedIndustry, selectedState, sortBy]);

  const tabbedList = useMemo(() => {
    if (activeTab === TAB_FEATURED) return filteredList.filter((i) => isFeaturedManufacturer(i));
    if (activeTab === TAB_SUBMITTED) return filteredList.filter((i) => isQuoteSubmitted(i));
    return filteredList;
  }, [activeTab, filteredList]);

  const featuredCount = useMemo(() => filteredList.filter((i) => isFeaturedManufacturer(i)).length, [filteredList]);
  const submittedCount = useMemo(() => filteredList.filter((i) => isQuoteSubmitted(i)).length, [filteredList]);

  const clearFilters = () => {
    setSearch("");
    setSortBy("relevance");
    setSelectedIndustry("");
    setSelectedState("");
  };

  return (
    <div className="cm-browse-page">
      <div className="cm-browse-container">
        <div className="cm-browse-hero">
          <div>
            <h1 className="cm-browse-title">Find Contract Manufacturers</h1>
            <p className="cm-browse-subtitle">Browse verified manufacturers and request quotes</p>
          </div>
          <div className="cm-browse-count">{!loading && !error ? `${tabbedList.length} Manufacturers Found` : ""}</div>
        </div>

        {error && (
          <div className="alert alert-warning mb-3" role="alert">
            {error}
          </div>
        )}

        <div className="cm-browse-filters">
          <div className="cm-browse-search">
            <FaSearch className="cm-browse-search-icon" />
            <input
              type="text"
              className="cm-browse-search-input"
              placeholder="Search by name or location..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="cm-browse-filter-row">
            <div className="cm-browse-filter">
              <span className="cm-browse-filter-label">Sort by</span>
              <select className="cm-browse-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="relevance">Highest Rating</option>
                <option value="name">Name (A–Z)</option>
                <option value="rating">Rating (high to low)</option>
              </select>
            </div>

            <div className="cm-browse-filter">
              <span className="cm-browse-filter-label">Industry</span>
              <select className="cm-browse-select" value={selectedIndustry} onChange={(e) => setSelectedIndustry(e.target.value)}>
                <option value="">All Industries</option>
                {INDUSTRY_TYPE_MAP.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="cm-browse-filter">
              <span className="cm-browse-filter-label">Location</span>
              <select className="cm-browse-select" value={selectedState} onChange={(e) => setSelectedState(e.target.value)}>
                <option value="">All Locations</option>
                {uniqueStates.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </div>

            <div className="cm-browse-filter-actions">
              <button type="button" className="cm-browse-clear" onClick={clearFilters}>
                Clear
              </button>
            </div>
          </div>
        </div>

        <div className="cm-browse-tabs">
          <button type="button" className={`cm-browse-tab ${activeTab === TAB_ALL ? "active" : ""}`} onClick={() => setActiveTab(TAB_ALL)}>
            All Manufacturers <span className="cm-browse-tab-count">({filteredList.length})</span>
          </button>
          <button
            type="button"
            className={`cm-browse-tab ${activeTab === TAB_SUBMITTED ? "active" : ""}`}
            onClick={() => setActiveTab(TAB_SUBMITTED)}
          >
            Submitted Quotes <span className="cm-browse-tab-count">({submittedCount})</span>
          </button>
          <button
            type="button"
            className={`cm-browse-tab ${activeTab === TAB_FEATURED ? "active" : ""}`}
            onClick={() => setActiveTab(TAB_FEATURED)}
          >
            Featured <span className="cm-browse-tab-count">({featuredCount})</span>
          </button>
        </div>

        <div className="cm-browse-grid">
          {loading ? (
            <div className="text-center py-5">Loading...</div>
          ) : (
            <div className="row g-4">
              {tabbedList.map((item) => {
                const isVerified = String(item?.verification_status || "").toLowerCase() === "verified";
                const featured = isFeaturedManufacturer(item);
                const rating = getRatingDisplay(item);
                const reviews = getReviewCount(item);
                const locationStr = getLocation(item);
                const tags = getIndustryTags(item);
                const submitted = isQuoteSubmitted(item);
                const quoteMeta = getQuoteSubmittedMeta(item);
                const capacityStr = getCapacity(item);
                const workforceStr = getWorkforceSummary(item);
                const experienceStr = getExperienceSummary(item);
                const moqStr = getMoqSummary(item, dimensionUnitOptions);
                const responseStr = getResponseTime(item);
                const certLabels = getCertificationShortLabels(item, 5);
                const rfqSent = rfqSentCompanyIds.has(Number(item.company_id));
                return (
                  <div key={item.company_id} className="col-12 col-md-6">
                    <div className={`cm-browse-card${featured ? " cm-browse-card--featured" : ""}`}>
                      <div className="cm-browse-card-head">
                        <div>
                          <div className="cm-browse-card-title">
                            {item.company_name || "Manufacturer"}
                            {isVerified && (
                              <span className="cm-browse-pill verified">
                                <FaCheckCircle aria-hidden className="cm-browse-pill-icon" />
                                Verified
                              </span>
                            )}
                            {featured && (
                              <span className="cm-browse-pill featured">
                                <FaStar aria-hidden className="cm-browse-pill-icon" />
                                Featured
                              </span>
                            )}
                          </div>
                          <div className="cm-browse-card-meta">
                            <span className="cm-browse-card-loc">
                              <FaMapMarkerAlt aria-hidden /> {locationStr || "Location not specified"}
                            </span>
                            <span className="cm-browse-card-rating">
                              <FaStar aria-hidden />
                              {rating != null ? `${rating}${reviews != null ? ` (${reviews})` : ""}` : "—"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {submitted && (
                        <div className="cm-browse-quote-banner">
                          <div className="cm-browse-quote-banner-main">
                            <FaFileAlt className="cm-browse-quote-icon" aria-hidden />
                            <div>
                              <div className="cm-browse-quote-title">Quote Submitted</div>
                              {quoteMeta.dateStr && (
                                <div className="cm-browse-quote-sub">Submitted on {quoteMeta.dateStr}</div>
                              )}
                            </div>
                          </div>
                          {quoteMeta.priceStr && <span className="cm-browse-quote-amount">{quoteMeta.priceStr}</span>}
                        </div>
                      )}

                      <div className="cm-browse-card-body">
                        {tags.length > 0 && (
                          <div className="cm-browse-card-tags">
                            {tags.map((t) => (
                              <span key={t} className="cm-browse-tag">
                                {t}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="cm-browse-metrics">
                          <div className="cm-browse-metric">
                            <FaIndustry className="cm-browse-metric-ico" aria-hidden />
                            <div>
                              <div className="cm-browse-metric-label">Capacity</div>
                              <div className="cm-browse-metric-value">{formatMetric(capacityStr)}</div>
                            </div>
                          </div>
                          <div className="cm-browse-metric">
                            <FaUsers className="cm-browse-metric-ico" aria-hidden />
                            <div>
                              <div className="cm-browse-metric-label">Workforce</div>
                              <div className="cm-browse-metric-value">{formatMetric(workforceStr)}</div>
                            </div>
                          </div>
                          <div className="cm-browse-metric">
                            <FaChartLine className="cm-browse-metric-ico" aria-hidden />
                            <div>
                              <div className="cm-browse-metric-label">Experience</div>
                              <div className="cm-browse-metric-value">{formatMetric(experienceStr)}</div>
                            </div>
                          </div>
                          <div className="cm-browse-metric">
                            <FaCube className="cm-browse-metric-ico" aria-hidden />
                            <div>
                              <div className="cm-browse-metric-label">MOQ</div>
                              <div className="cm-browse-metric-value">{formatMetric(moqStr)}</div>
                            </div>
                          </div>
                        </div>

                        {(certLabels.length > 0 || responseStr) && (
                          <div className="cm-browse-card-extra">
                            {certLabels.length > 0 && (
                              <div className="cm-browse-certs">
                                <span className="cm-browse-certs-label">
                                  <FaAward aria-hidden />
                                  Certifications
                                </span>
                                <div className="cm-browse-cert-pills">
                                  {certLabels.map((c) => (
                                    <span key={c} className="cm-browse-cert-pill">
                                      {c}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {responseStr && (
                              <div className="cm-browse-response">
                                <span className="cm-browse-response-dot" aria-hidden />
                                Typical response time: <strong>{responseStr}</strong>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="cm-browse-card-actions">
                          <Link to={`/contract-manufacturers/profile/${item.company_id}`} className="cm-browse-action muted">
                            <FaEye className="me-1" aria-hidden />
                            View Profile
                          </Link>
                          {submitted ? (
                            <Link to={`/dashboard/buyer/contract-manufacturers/quotes?companyId=${item.company_id}`} className="cm-browse-action primary">
                              <FaFileAlt className="me-1" aria-hidden />
                              View Quotes
                            </Link>
                          ) : rfqSent ? (
                            <button type="button" className="cm-browse-action sent" disabled>
                              <FaCheckCircle className="me-1" aria-hidden />
                              RFQ Sent
                            </button>
                          ) : (
                            <Link to={`/contract-manufacturers/rfq/${item.company_id}`} className="cm-browse-action primary">
                              <FaPaperPlane className="me-1" aria-hidden />
                              Request Quote
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {!loading && tabbedList.length === 0 && <div className="text-center py-5 text-muted">No contract manufacturers found.</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

